import React from 'react';
import { MoreHorizontal } from 'react-feather';
import { useTranslation } from 'react-i18next';
import TableSettings from '@components-shared/table/TableSettings';
import { NUMBER_OF_ROWS } from '@components-shared/table/constants';
import { keyMap } from '@locales/lokalise/keyMap';
import { Table } from '@tanstack/react-table';
import { getFormattedNumber } from '@utils/helper';
import cn from 'classnames';
import type { IGridContextPagination } from '../GridContext';
import styles from './gridPagination.module.scss';

export interface GridPaginationProps {
  table: Pick<
    Table<unknown>,
    | 'getPageCount'
    | 'setPageSize'
    | 'getCanPreviousPage'
    | 'getCanNextPage'
    | 'previousPage'
    | 'nextPage'
    | 'setPageIndex'
  >;
  pagination: IGridContextPagination;
  totalCount: number;
  isCountNotAccurate?: boolean;
  numberOfRowsList?: number[];
  showTableSettings?: boolean;
}

export const GridPagination = ({
  table,
  pagination,
  totalCount,
  isCountNotAccurate,
  numberOfRowsList,
  showTableSettings = true,
}: GridPaginationProps) => {
  const { i18n } = useTranslation();

  const generatePageRange = () => {
    const delta = 2;
    const selectedPage = pagination.pageIndex;
    const pageCount = table.getPageCount();

    return new Array(Math.max(pageCount, 0))
      .fill('')
      .map((_, index) => index)
      .filter((page) => {
        if (page === 0 || page === pageCount - 1) {
          return true;
        }

        return selectedPage <= page + delta && selectedPage >= page - delta;
      });
  };

  const renderTotalCount = () => {
    if (pagination.pageSize < 1) {
      return null;
    }

    const from = pagination.pageSize * pagination.pageIndex + 1;
    const to = pagination.pageSize * (pagination.pageIndex + 1);

    const formattedFrom = getFormattedNumber(from);
    const formattedTo = getFormattedNumber(Math.min(to, totalCount));
    const formattedOf = `${getFormattedNumber(totalCount)}${isCountNotAccurate ? '+' : ''}`;

    return (
      <div className={styles.totalCount}>
        {i18n.t(keyMap.tableTotalCount, {
          from: formattedFrom,
          to: formattedTo,
          of: formattedOf,
        })}
      </div>
    );
  };

  return (
    <div className={styles.paginationContainer}>
      {showTableSettings && (
        <TableSettings
          numberOfRowsList={numberOfRowsList ?? NUMBER_OF_ROWS}
          numberOfRows={pagination.pageSize}
          changeRowsPerPage={(numberOfRows) => table.setPageSize(numberOfRows)}
        />
      )}

      <div className="pagination">
        {renderTotalCount()}

        <button
          type="button"
          className={cn('pagination__item item--arrow', { disabled: !table.getCanPreviousPage() })}
          onClick={() => table.previousPage()}>
          <span className="pagination__item-icon" />
        </button>

        {generatePageRange().map((page, index, arr) => {
          const isLastElement = arr.length - 1 === index;

          return (
            <React.Fragment key={index}>
              {index > 0 && arr[index - 1] + 1 !== page && (
                <button className={cn('pagination__item', { disabled: true })}>
                  <MoreHorizontal size={12} />
                </button>
              )}
              <button
                key={index}
                type="button"
                onClick={() => table.setPageIndex(page ?? 0)}
                className={cn('pagination__item', {
                  active: page === pagination.pageIndex,
                })}>
                {page + 1}
                {isLastElement && isCountNotAccurate && '+'}
              </button>
            </React.Fragment>
          );
        })}

        <button
          className={cn('pagination__item item--arrow', { disabled: !table.getCanNextPage() })}
          type="button"
          onClick={() => table.nextPage()}>
          <span className="pagination__item-icon icon--next" />
        </button>
      </div>
    </div>
  );
};
